"use client";

import { sanitiseUrl, track } from "@/utilities/analytics";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";

/**
 * Emits `virtual_page_view` on initial load and on every client-side route
 * change. The app owns page views entirely — automatic page_view is disabled in
 * the GA4 configuration tag.
 *
 * Deliberately keyed on pathname only. `/apply-online` re-pushes its query
 * string on every address and plan lookup, so firing on any history change
 * would count several page views for one page. Query params are read straight
 * off `window.location` rather than via `useSearchParams()`, which would both
 * re-fire this effect and force a Suspense boundary in the root layout.
 */
export const RouteTracker = () => {
	const pathname = usePathname();
	const previousUrl = useRef<string | null>(null);

	useEffect(() => {
		// The document title is set by Next after the route commits, so read it
		// on the next tick or every soft navigation reports the previous page's
		// title.
		const timeout = window.setTimeout(() => {
			const currentUrl = window.location.href;

			track({
				event: "virtual_page_view",
				page_path: sanitiseUrl(currentUrl),
				page_title: document.title,
				page_referrer: sanitiseUrl(previousUrl.current ?? document.referrer),
			});

			previousUrl.current = currentUrl;
		}, 0);

		return () => window.clearTimeout(timeout);
	}, [pathname]);

	return null;
};
